import React from 'react';
import { Alert, Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';

import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';

import { FProcessStageBar } from '../workbench';
import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import FPlanningJourney from '../core/FPlanningJourney';

import SimulationManager from './SimulationManager';
import SimulationAssumptionsGrid from './SimulationAssumptionsGrid';
import SimulationExecutivePanel from './SimulationExecutivePanel';
import SimulationFinancialAnalysisPanel from './SimulationFinancialAnalysisPanel';
import SimulationMixerPanel from './SimulationMixerPanel';
import SimulationObjectiveImpactPanel from './SimulationObjectiveImpactPanel';
import SimulationObjectiveGapPanel from './SimulationObjectiveGapPanel';
import SimulationReviewPanel from './SimulationReviewPanel';
import SimulationSensitivityPanel from './SimulationSensitivityPanel';
import SimulationIssuesGrid from './SimulationIssuesGrid';
import SimulationTabs from './SimulationTabs';
import SimulationImpactBar from './SimulationImpactBar';

import {
  acknowledgeExecutiveReturn,
  //changeSimulationTargetSet,
  fetchSimulationObjectiveImpact,
  createSimulationScenario,
  fetchSimulationManager,
  fetchSimulationScenarioWorkbench,
  fetchCurrentSimulationObjectiveGap,
  fetchSimulationSensitivity,
  fetchSimulationObjectiveGapWorkbench,
  startSimulationObjectiveGap,
  setSimulationObjectiveGapMode,
  evaluateSimulationObjectiveGap,
  recomputeSimulation,
  resubmitSimulationToExecutiveReview,
  runSimulationSensitivity,
  saveSimulationAssumptions,
  saveSimulationDrivers,
  submitSimulationToExecutiveReview,
} from './simulation.apis';

import {
  //ISensitivityControl,
  ISimulationAssumptionRow,
  ISimulationCreateScenarioRequest,
  ISimulationDriver,
  ISimulationManagerWorkbench,
  ISimulationScenarioRow,
  ISimulationTargetSetOption,
  ISimulationWorkbench,
  IObjectiveGapWorkbench,
  ISensitivityWorkbench,
  IObjectiveGapEvaluation,
  ObjectiveGapComputeMode,
  IObjectiveImpactWorkbench,
  IObjectiveImpactTargetSetOption,
  SimulationTabCode,
} from './simulation.models';

type SimulationMode = 'AUTO' | 'MANUAL';

type State = {
  activeTab: SimulationTabCode;
  simulationMode: SimulationMode;
  loading: boolean;
  error: string;
  success: string;
  warning: string;
  phases: any[];
  manager: ISimulationManagerWorkbench | null;
  workbench: ISimulationWorkbench | null;
  selectedScenarioId: number | null;
  mixerDirty: boolean;
  autoProcessing: boolean;
  objectiveGap: IObjectiveGapWorkbench | null;
  objectiveGapLastEvaluation: IObjectiveGapEvaluation | null;
  objectiveGapLoading: boolean;
  objectiveGapChecked: boolean;
  sensitivityWorkbench: ISensitivityWorkbench | null;
  sensitivityLoading: boolean;
  sensitivityChecked: boolean;
  objectiveImpact: IObjectiveImpactWorkbench | null;
  objectiveImpactLoading: boolean;
  objectiveImpactChecked: boolean;
};

class InteractiveSimulationWorkbench extends React.Component<PlanningPageProps, State> {
  state: State = {
    activeTab: 'executive',
    simulationMode: 'MANUAL',
    loading: false,
    error: '',
    success: '',
    warning: '',
    phases: [],
    manager: null,
    workbench: null,
    selectedScenarioId: null,
    mixerDirty: false,
    autoProcessing: false,
    objectiveGap: null,
    objectiveGapLastEvaluation: null,
    objectiveGapLoading: false,
    objectiveGapChecked: false,
    sensitivityWorkbench: null,
    sensitivityLoading: false,
    sensitivityChecked: false,
    objectiveImpact: null,
    objectiveImpactLoading: false,
    objectiveImpactChecked: false,
  };

  componentDidMount() {
    void this.initialize();
  }

  private getPageParam = (key: string): unknown => {
    const params: any = this.props.params ?? {};
    return params[key] ?? params?.get?.(key) ?? undefined;
  };

  private getPlanningCycleId = (): number => {
    const value =
      this.props.plancontext?.pci ??
      this.getPageParam('planningcycleid') ??
      this.getPageParam('pci');
    return Number(value || 0);
  };

  private initialize = async () => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid) {
      this.setState({
        error: 'Planning Cycle context is missing. Set a preferred Planning Cycle and reopen Simulation.',
      });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      const phases = await fetchPlanningJourneyPhases();
      this.setState({ phases });

      const requestedScenarioId = Number(
        this.getPageParam('scenarioid') ?? this.getPageParam('sci') ?? 0,
      );

      await this.loadManager(false);

      if (requestedScenarioId > 0) {
        await this.openScenarioById(requestedScenarioId);
      }
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private loadManager = async (preserveMessage = true) => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid) return;

    this.setState({
      loading: true,
      error: '',
      warning: '',
      success: preserveMessage ? this.state.success : '',
    });

    try {
      const manager = await fetchSimulationManager(planningcycleid);
      this.setState({ manager });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
      throw error;
    } finally {
      this.setState({ loading: false });
    }
  };

  private loadScenario = async (scenarioid: number, preserveMessage = true) => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid || !scenarioid) return;

    this.setState({
      loading: true,
      error: '',
      warning: '',
      success: preserveMessage ? this.state.success : '',
      sensitivityWorkbench: null,
      sensitivityLoading: false,
      sensitivityChecked: false,
    });

    try {
      const workbench = await fetchSimulationScenarioWorkbench(
        planningcycleid,
        scenarioid,
      );

      this.setState({
        workbench,
        selectedScenarioId: scenarioid,
        activeTab: this.state.workbench ? this.state.activeTab : 'executive',
        mixerDirty: false,
        objectiveImpact: null,
        objectiveImpactLoading: false,
        objectiveImpactChecked: false,
        objectiveGap: null,
        objectiveGapLastEvaluation: null,
        objectiveGapChecked: false,
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
      throw error;
    } finally {
      this.setState({ loading: false });
    }
  };

  private refresh = async () => {
    if (this.state.selectedScenarioId) {
      await this.loadScenario(this.state.selectedScenarioId, true);
    } else {
      await this.loadManager(true);
    }
  };

  private openScenarioById = async (scenarioid: number) => {
    const setParam = this.props.onParam('set');
    setParam('scenarioid', scenarioid);
    await this.loadScenario(scenarioid, false);
  };

  private openScenario = async (scenario: ISimulationScenarioRow) => {
    await this.openScenarioById(scenario.rid);
  };

  private returnToManager = async () => {
    const setParam = this.props.onParam('set');
    setParam('scenarioid', null);
    this.setState({
      workbench: null,
      selectedScenarioId: null,
      activeTab: 'executive',
      success: '',
      warning: '',
      error: '',
      mixerDirty: false,
      objectiveGap: null,
      objectiveGapLastEvaluation: null,
      objectiveGapChecked: false,
      objectiveImpact: null,
      objectiveImpactLoading: false,
      objectiveImpactChecked: false,
      sensitivityWorkbench: null,
      sensitivityLoading: false,
      sensitivityChecked: false,
    });
    await this.loadManager(false);
  };

  private createScenario = async (request: ISimulationCreateScenarioRequest) => {
    this.setState({ loading: true, error: '', success: '', warning: '' });

    try {
      const result = await createSimulationScenario(request);
      this.setState({
        success: result.baseline
          ? 'Baseline Simulation scenario created from the published Forecast.'
          : 'Alternative Simulation scenario created from the selected parent.',
      });
      await this.loadManager(true);
      await this.openScenarioById(result.scenarioid);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
      throw error;
    } finally {
      this.setState({ loading: false });
    }
  };

  private requireWorkbench = (): ISimulationWorkbench => {
    if (!this.state.workbench) {
      throw new Error('Simulation Scenario workbench is not loaded.');
    }
    return this.state.workbench;
  };

  private mutateScenario = async (
    message: string,
    action: () => Promise<any>,
  ) => {
    const scenarioid = this.state.selectedScenarioId;
    if (!scenarioid) return;

    this.setState({ loading: true, error: '', success: '', warning: '' });

    try {
      await action();
      this.setState({ success: message });
      await this.loadScenario(scenarioid, true);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private saveAssumptions = async (rows: ISimulationAssumptionRow[]) => {
    const w = this.requireWorkbench();
    const dirty = rows.filter(
      (x) => x.__dirty || (x as any)._state === 'dirty' || (x as any)._state === 'new',
    );

    if (!dirty.length) {
      this.setState({ warning: 'There are no changed assumptions to save.' });
      return;
    }

    await this.mutateScenario(
      'Scenario assumptions saved. Recompute before submission.',
      () => saveSimulationAssumptions(
        w.context.planningcycleid,
        w.context.scenarioid,
        dirty,
      ),
    );
  };

  private saveDrivers = async (drivers: ISimulationDriver[]) => {
    const w = this.requireWorkbench();

    await this.mutateScenario(
      'Driver changes saved. Recompute before submission.',
      () => saveSimulationDrivers(
        w.context.planningcycleid,
        w.context.scenarioid,
        drivers,
      ),
    );
  };

  private autoCommitDrivers = async (drivers: ISimulationDriver[]): Promise<void> => {
    const w = this.requireWorkbench();

    if (!drivers.length) return;

    this.setState({
      autoProcessing: true,
      error: '',
      warning: '',
      success: '',
    });

    try {
      /*
       * AUTO mode deliberately uses the same governed persistence and
       * recomputation path as MANUAL mode. The only difference is that
       * the UI coordinates the sequence automatically after the driver
       * control settles.
       */
      await saveSimulationDrivers(
        w.context.planningcycleid,
        w.context.scenarioid,
        drivers,
      );

      await recomputeSimulation(
        w.context.planningcycleid,
        w.context.scenarioid,
      );

      await this.loadScenario(w.context.scenarioid, false);
    } catch (error: any) {
      const message = error?.message || 'Automatic scenario recomputation failed.';
      this.setState({ error: message });
      throw error;
    } finally {
      this.setState({ autoProcessing: false });
    }
  };

  // private recompute0 = async () => {
  //   const w = this.requireWorkbench();

  //   await this.mutateScenario(
  //     'Scenario recomputed.',
  //     () => recomputeSimulation(
  //       w.context.planningcycleid,
  //       w.context.scenarioid,
  //     ),
  //   );
  // };

  private recompute = async (): Promise<void> => {
    const w = this.requireWorkbench();
    if (!w) return;
    this.setState({loading: true,error: '',success: ''});

    try {
      await recomputeSimulation(
        w.context.planningcycleid,
        w.context.scenarioid,
      );

      this.setState({ success: 'Scenario recomputed successfully.'});

      await this.loadScenario(w.context.scenarioid,false);

    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Scenario recomputation failed.',
      });
    }
  };

  // private recompute1 = async (): Promise<void> => {
  //   //const { planningcycleid, scenarioid } = this.state;
  //   const w = this.requireWorkbench();

  //   this.setState({ loading: true });

  //   try {
  //     const response = await recomputeSimulation(
  //       w.context.planningcycleid,
  //       w.context.scenarioid,
  //     );

  //     //recomputeSimulation() in api returns a normalized model, not server flags
  //     // this must be handled in api using centralized error handling 

  //     // if (response?.rc !== 0) {
  //     //   this.notifyError(
  //     //     response?.em || 'Scenario recomputation failed.',
  //     //   );
  //     //   return;
  //     // }

  //     const sd:any = response; //response.sd || {};

  //     this.setState((state) => ({
  //       loading: false,

  //       workbench: {
  //         ...state.workbench,

  //         executive:
  //           sd.executive ??
  //           state.workbench.executive,

  //         scenario: {
  //           ...state.workbench.scenario,
  //           statecode:
  //             sd.statecode ??
  //             state.workbench.scenario.statecode,

  //           requiresrecompute:
  //             sd.requiresrecompute ??
  //             false,

  //           computationcurrent: true,

  //           lastcomputedat:
  //             sd.computedat ??
  //             state.workbench.scenario.lastcomputedat,
  //         },

  //         actions: {
  //           ...state.workbench.actions,
  //           canRecompute: false,
  //           canRunSensitivity: true,
  //         },
  //       },

  //       pendingScenarioChanges: false,
  //     }));

  //     // Then refresh the complete governed workbench
  //     // so assumptions, financials, drivers, etc. are all synchronized.
  //     await this.loadScenarioWorkbench();

  //   } catch (error) {
  //     this.setState({ loading: false });
  //     this.notifyError('Scenario recomputation failed.');
  //   }
  // };

  private loadCurrentObjectiveGap = async (force = false): Promise<void> => {
    const w = this.state.workbench;
    if (!w || (!force && this.state.objectiveGapChecked)) return;

    this.setState({ objectiveGapLoading: true, error: '' });

    try {
      const current = await fetchCurrentSimulationObjectiveGap(w.context.scenariocode);
      const sessioncode = current?.session?.sessioncode ?? current?.sessioncode ?? null;

      if (!sessioncode) {
        this.setState({
          objectiveGap: null,
          objectiveGapLastEvaluation: null,
          objectiveGapChecked: true,
        });
        return;
      }

      const objectiveGap = await fetchSimulationObjectiveGapWorkbench(sessioncode);
      this.setState({ objectiveGap, objectiveGapChecked: true });
    } catch (error: any) {
      this.setState({ error: error?.message || 'Objective Gap workbench could not be loaded.' });
    } finally {
      this.setState({ objectiveGapLoading: false });
    }
  };

  private startObjectiveGap = async (
    targetvalue: number,
    mode: ObjectiveGapComputeMode,
  ): Promise<void> => {
    const w = this.requireWorkbench();
    this.setState({ objectiveGapLoading: true, error: '', warning: '', success: '' });

    try {
      const started = await startSimulationObjectiveGap(
        w.context.scenariocode,
        targetvalue,
        mode,
      );
      const sessioncode = started?.session?.sessioncode;
      if (!sessioncode) throw new Error('Objective Gap start did not return a session code.');

      const objectiveGap = await fetchSimulationObjectiveGapWorkbench(sessioncode);
      this.setState({
        objectiveGap,
        objectiveGapLastEvaluation: null,
        objectiveGapChecked: true,
        success: 'Objective Gap analytical workspace prepared.',
      });
    } catch (error: any) {
      this.setState({ error: error?.message || 'Objective Gap could not be started.' });
      throw error;
    } finally {
      this.setState({ objectiveGapLoading: false });
    }
  };

  private setObjectiveGapMode = async (mode: ObjectiveGapComputeMode): Promise<void> => {
    const sessioncode = this.state.objectiveGap?.session?.sessioncode;
    if (!sessioncode) return;

    this.setState({ objectiveGapLoading: true, error: '' });
    try {
      await setSimulationObjectiveGapMode(sessioncode, mode);
      const objectiveGap = await fetchSimulationObjectiveGapWorkbench(sessioncode);
      this.setState({ objectiveGap });
    } catch (error: any) {
      this.setState({ error: error?.message || 'Objective Gap mode could not be changed.' });
      throw error;
    } finally {
      this.setState({ objectiveGapLoading: false });
    }
  };

  private evaluateObjectiveGap = async (
    drivercode: string,
    value: number,
  ): Promise<void> => {
    const sessioncode = this.state.objectiveGap?.session?.sessioncode;
    if (!sessioncode) throw new Error('Start an Objective Gap session first.');

    this.setState({ objectiveGapLoading: true, error: '', warning: '' });
    try {
      const evaluation = await evaluateSimulationObjectiveGap(sessioncode, drivercode, value);
      const objectiveGap = await fetchSimulationObjectiveGapWorkbench(sessioncode);
      this.setState({
        objectiveGap,
        objectiveGapLastEvaluation: evaluation,
      });
    } catch (error: any) {
      this.setState({ error: error?.message || 'Objective Gap evaluation failed.' });
      throw error;
    } finally {
      this.setState({ objectiveGapLoading: false });
    }
  };

  private loadSensitivity = async (
    force = false,
  ): Promise<void> => {

    const w = this.state.workbench;

    if (!w) return;

    if (
      this.state.sensitivityChecked &&
      !force
    ) {
      return;
    }

    this.setState({
      sensitivityLoading: true,
      error: '',
    });

    try {

      const sensitivityWorkbench =
        await fetchSimulationSensitivity(
          w.context.scenariocode,
        );

      this.setState({
        sensitivityWorkbench,
        sensitivityChecked: true,
      });

    } catch (error: any) {

      this.setState({
        error:
          error?.message ||
          'Sensitivity Analysis could not be loaded.',
      });

    } finally {

      this.setState({
        sensitivityLoading: false,
      });

    }
  };

  // private runSensitivity = async (records: ISensitivityControl[]) => {
  //   const w = this.requireWorkbench();

  //   await this.mutateScenario(
  //     'Sensitivity analysis completed.',
  //     () => runSimulationSensitivity(
  //       w.context.planningcycleid,
  //       w.context.scenarioid,
  //       records,
  //     ),
  //   );
  // };

  private runSensitivity = async (
    targetdrivercode: string,
    deltapercent: number,
  ): Promise<void> => {

    const w = this.requireWorkbench();

    this.setState({
      sensitivityLoading: true,
      error: '',
      warning: '',
      success: '',
    });

    try {

      await runSimulationSensitivity(
        w.context.scenariocode,
        targetdrivercode,
        deltapercent,
      );

      const sensitivityWorkbench =
        await fetchSimulationSensitivity(
          w.context.scenariocode,
        );

      this.setState({
        sensitivityWorkbench,
        sensitivityChecked: true,
        success: 'Sensitivity analysis completed.',
      });

    } catch (error: any) {

      this.setState({
        error:
          error?.message ||
          'Sensitivity analysis failed.',
      });

    } finally {

      this.setState({
        sensitivityLoading: false,
      });

    }
  };

  private loadObjectiveImpact = async (
    targetsetcode?: string | null,
    force = false,
  ): Promise<void> => {

    const w = this.state.workbench;

    if (!w) return;


    if (
      this.state.objectiveImpactChecked &&
      !force &&
      !targetsetcode
    ) {
      return;
    }


    this.setState({
      objectiveImpactLoading: true,
      error: '',
    });


    try {

      const objectiveImpact =
        await fetchSimulationObjectiveImpact(
          w.context.scenariocode,
          targetsetcode,
        );


      this.setState({
        objectiveImpact,
        objectiveImpactChecked: true,
      });


    } catch (error: any) {

      this.setState({
        error:
          error?.message ||
          'Objective Impact could not be loaded.',
      });


    } finally {

      this.setState({
        objectiveImpactLoading: false,
      });

    }
  };

  // private targetSetChange = async (targetset: ISimulationTargetSetOption) => {
  //   const w = this.requireWorkbench();

  //   await this.mutateScenario(
  //     'Target set changed.',
  //     () => changeSimulationTargetSet(
  //       w.context.planningcycleid,
  //       w.context.scenarioid,
  //       targetset.rid,
  //       targetset.code,
  //     ),
  //   );
  // };

  private objectiveImpactTargetSetChange = async (
    targetset: IObjectiveImpactTargetSetOption,
  ): Promise<void> => {

    await this.loadObjectiveImpact(
      targetset.shc,
      true,
    );
  };

  private submit = async (notes: string) => {
    const w = this.requireWorkbench();

    await this.mutateScenario(
      'Simulation frozen and submitted to Executive Review.',
      () => submitSimulationToExecutiveReview(
        w.context.planningcycleid,
        w.context.scenarioid,
        notes,
      ),
    );
  };

  private resubmit = async (notes: string) => {
    const w = this.requireWorkbench();

    if (!w.submission.submissionid) {
      throw new Error('A returned submission is required for resubmission.');
    }

    await this.mutateScenario(
      'Revised Simulation resubmitted to Executive Review.',
      () => resubmitSimulationToExecutiveReview(
        w.context.planningcycleid,
        w.context.scenarioid,
        w.submission.submissionid!,
        notes,
      ),
    );
  };

  private acknowledgeReturn = async () => {
    const w = this.requireWorkbench();

    await this.mutateScenario(
      'Executive Review return acknowledged. Revise the scenario and recompute before resubmission.',
      () => acknowledgeExecutiveReturn(
        w.context.planningcycleid,
        w.context.scenarioid,
        'Executive Review return acknowledged from the Simulation workbench.',
      ),
    );
  };

  private openExecutiveReview = () => {
    const w = this.requireWorkbench();
    const setParam = this.props.onParam('set');

    setParam('planningcycleid', w.context.planningcycleid);
    setParam('scenarioid', w.context.scenarioid);
    if (w.context.scenarioix) setParam('scenarioix', w.context.scenarioix);

    if (w.submission.submissionid) {
      setParam('simulationsubmissionid', w.submission.submissionid);
    }
    if (w.submission.executiveReviewId) {
      setParam('executivereviewid', w.submission.executiveReviewId);
    }

    setParam('planningcyclephase', 'EXECUTIVE_REVIEW');
    this.props.setPage(w.actions.nextpage || 'executivereview');
  };

  // private renderScenarioContext0() {
  //   const c = this.state.workbench?.context;
  //   if (!c) return null;

  //   return (
  //     <Grid container spacing={1.5} alignItems="center">
  //       <Grid size={{ xs: 12, md: 4 }}>
  //         <Typography variant="caption" color="text.secondary">Planning Cycle</Typography>
  //         <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
  //           {c.planningcyclename || c.planningcyclecode}
  //         </Typography>
  //       </Grid>
  //       <Grid size={{ xs: 12, md: 4 }}>
  //         <Typography variant="caption" color="text.secondary">Scenario</Typography>
  //         <Stack direction="row" spacing={1} alignItems="center">
  //           <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
  //             {c.scenarioname || c.scenariocode}
  //           </Typography>
  //           {c.baseline && <Chip size="small" label="Baseline" color="info" variant="outlined" />}
  //         </Stack>
  //       </Grid>
  //       <Grid size={{ xs: 6, md: 2 }}>
  //         <Chip size="small" label={c.statecode} />
  //       </Grid>
  //       <Grid size={{ xs: 6, md: 2 }}>
  //         <Chip
  //           size="small"
  //           variant="outlined"
  //           label={c.fiscalyear || c.budgetperiodname || 'Current period'}
  //         />
  //       </Grid>
  //     </Grid>
  //   );
  // }

  private changeTab = (activeTab: SimulationTabCode) => {
    this.setState({ activeTab },
      () => {
        if (activeTab === 'sensitivity') {
          void this.loadSensitivity(false);
        }

        if (activeTab === 'objectivegap') {
          void this.loadCurrentObjectiveGap(false);
        }

        if (activeTab === 'objectives') {
          void this.loadObjectiveImpact(null,false);
        }
      },
    );
  };

  private renderScenarioContext() {
    const c = this.state.workbench?.context;
    if (!c) return null;

    return (
      <Grid container spacing={1.5} alignItems="center">
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Planning Cycle
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.planningcyclename || c.planningcyclecode}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Scenario
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {c.scenarioname || c.scenariocode}
            </Typography>
            {c.baseline && <Chip size="small" label="Baseline" color="info" variant="outlined" />}
          </Stack>
        </Grid>
        <Grid size={{ xs: 6, md: 1.5 }}>
          <Chip size="small" label={c.statecode} />
        </Grid>
        <Grid size={{ xs: 6, md: 1.5 }}>
          <Chip
            size="small"
            variant="outlined"
            label={c.fiscalyear || c.budgetperiodname || 'Current period'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Stack
            direction="row"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            alignItems="center"
            spacing={1}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700 }}
            >
              Simulation Mode
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="info"
              value={this.state.simulationMode}
              onChange={(_, simulationMode: SimulationMode | null) => {
                if (simulationMode) {
                  this.setState({ simulationMode });
                }
              }}
            >
              <Tooltip title="Live visual scenario exploration">
                <ToggleButton value="AUTO">
                  <AutoAwesomeIcon
                    fontSize="small"
                    sx={{ mr: 0.75 }}
                  />
                  Auto
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Detailed planning and controlled scenario adjustment">
                <ToggleButton value="MANUAL">
                  <TuneIcon
                    fontSize="small"
                    sx={{ mr: 0.75 }}
                  />
                  Manual
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Stack>
        </Grid>

      </Grid>
    );
  }

  private renderScenarioTab() {
    const w = this.state.workbench;
    if (!w) return null;

    const frozen =
      ['SUBMITTED', 'APPROVED'].includes(
        String(w?.context?.statecode || '').toUpperCase(),
      );

    switch (this.state.activeTab) {

      case 'assumptions':
        return (
          <SimulationAssumptionsGrid
            rows={w.assumptions}
            loading={this.state.loading}
            editable={w.actions.canEditAssumptions}
            onChange={(assumptions) =>
              this.setState({ workbench: { ...w, assumptions } })
            }
            onSave={this.saveAssumptions}
          />
        );

      case 'mixer':
        return (
          <SimulationMixerPanel
            mixer={w.mixer}
            loading={this.state.loading}
            editable={w.actions.canUpdateDrivers}
            canRecompute={w.actions.canRecompute}
            computeMode={this.state.simulationMode}
            autoProcessing={this.state.autoProcessing}
            onSaveDrivers={this.saveDrivers}
            onAutoCommit={this.autoCommitDrivers}
            onRecompute={this.recompute}
            onDriversChange={(_drivers, dirty) => this.setState({ mixerDirty: dirty })}
          />
        );

      case 'financials':
        return <SimulationFinancialAnalysisPanel data={w.financials} />;

      case 'objectives':
        return (
          <SimulationObjectiveImpactPanel
            workbench={this.state.objectiveImpact}
            loading={this.state.loading || this.state.objectiveImpactLoading}
            onTargetSetChange={this.objectiveImpactTargetSetChange}
            onRefresh={() =>
              this.loadObjectiveImpact(this.state.objectiveImpact?.targetset?.targetsetcode ?? null,true)
            }
          />
        );

      case 'objectivegap':
        return (
          <SimulationObjectiveGapPanel
            sourcePartialResult={w.executive.partialresult}
            workbench={this.state.objectiveGap}
            lastEvaluation={this.state.objectiveGapLastEvaluation}
            loading={this.state.loading || this.state.objectiveGapLoading}
            editable={!frozen}
            onStart={this.startObjectiveGap}
            onModeChange={this.setObjectiveGapMode}
            onEvaluate={this.evaluateObjectiveGap}
            onRefresh={() => this.loadCurrentObjectiveGap(true)}
          />
        );

      case 'sensitivity':
        return (
          <SimulationSensitivityPanel
            workbench={this.state.sensitivityWorkbench}
            loading={this.state.loading || this.state.sensitivityLoading}
            editable={!frozen}
            canRun={w.actions.canRunSensitivity}
            onRun={this.runSensitivity}
            onRefresh={() => this.loadSensitivity(true)}
          />
        );

      case 'review':
        return (
          <Stack spacing={2}>
            <SimulationIssuesGrid
              rows={w.issues}
              rowCount={w.issues.length}
              severity=""
              paginationModel={{ page: 0, pageSize: Math.max(25, w.issues.length || 25) }}
              loading={this.state.loading}
              onRefresh={this.refresh}
              onSeverityChange={() => undefined}
              onPaginationModelChange={() => undefined}
            />
            <SimulationReviewPanel
              readiness={w.readiness}
              submission={w.submission}
              actions={w.actions}
              loading={this.state.loading}
              onSubmit={this.submit}
              onResubmit={this.resubmit}
              onAcknowledgeReturn={this.acknowledgeReturn}
              onOpenExecutiveReview={this.openExecutiveReview}
            />
          </Stack>
        );

      default:
        return <SimulationExecutivePanel data={w.executive} />;
    }
  }

  render() {
    const w = this.state.workbench;
    const manager = this.state.manager;
    const inScenario = Boolean(w && this.state.selectedScenarioId);
    // const scenarioFrozen =
    //   ['SUBMITTED', 'APPROVED'].includes(
    //     String(w?.context?.simulationstate || '').toUpperCase(),
    //   );

    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={inScenario ? this.returnToManager : this.props.onBack}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            currentPhaseCode={(w?.context.phasecode || manager?.context.phasecode || 'SIMULATION')}
          />
        }
        title={inScenario ? 'BIE Simulation Scenario' : 'BIE Simulation'}
        subtitle={
          inScenario
            ? 'Scenario assumptions, driver adjustments, financial analysis and governed review'
            : 'Create and manage Planning-Cycle Simulation scenarios from the published Forecast Baseline'
        }
        icon={<AutoGraphIcon />}
        loading={this.state.loading}
        onRefresh={this.refresh}
        summary={inScenario ? this.renderScenarioContext() : undefined}
      >
        <Stack spacing={2}>
          <FProcessStageBar
            activeCode="SIMULATION"
            stages={[
              { code: 'HISTORICAL', label: 'Historical' },
              { code: 'FORECASTING', label: 'Forecasting' },
              { code: 'SIMULATION', label: 'Simulation' },
              { code: 'EXECUTIVE_REVIEW', label: 'Executive Review' },
              { code: 'DETERMINANT_BUDGET', label: 'Determinant Budget' },
              { code: 'PLANNING', label: 'Planning' },
            ]}
          />

          {this.state.error && <Alert severity="error">{this.state.error}</Alert>}
          {this.state.success && <Alert severity="success">{this.state.success}</Alert>}
          {this.state.warning && <Alert severity="warning">{this.state.warning}</Alert>}

          {!inScenario && manager && (
            <SimulationManager
              workbench={manager}
              loading={this.state.loading}
              onRefresh={this.refresh}
              onCreate={this.createScenario}
              onOpen={this.openScenario}
            />
          )}

          {inScenario && w && (
            <>
              {w.executivereturn?.returned && !w.executivereturn.acknowledged && (
                <Alert severity="warning" icon={<AssignmentReturnIcon />}>
                  <Typography sx={{ fontWeight: 800 }}>Returned by Executive Review</Typography>
                  <Typography variant="body2">
                    {w.executivereturn.reason || 'Executive Review returned this scenario for revision.'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    The scenario is frozen until the return is acknowledged.
                  </Typography>
                </Alert>
              )}

              {w.executivereturn?.returned && w.executivereturn.acknowledged && (
                <Alert severity={w.executivereturn.resubmissionready ? 'success' : 'info'}>
                  <Typography sx={{ fontWeight: 800 }}>
                    {w.executivereturn.resubmissionready
                      ? 'Executive Review revision is ready for resubmission'
                      : 'Scenario under Executive Review revision'}
                  </Typography>
                  <Typography variant="body2">
                    {w.executivereturn.reason || 'Executive Review requested revision of this scenario.'}
                  </Typography>
                  {!w.executivereturn.resubmissionready && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Revise the requested inputs and recompute the scenario before resubmission.
                    </Typography>
                  )}
                </Alert>
              )}

              {w.readiness.stale && (
                <Alert severity="warning">
                  Inputs changed after the last computation. Recompute before submission.
                </Alert>
              )}
              <SimulationImpactBar
                executive={w.executive}
                dirty={this.state.mixerDirty}
                stale={w.readiness.stale}
              />

              <Alert severity="info">
                Published Forecast values form the governed baseline. Simulation may adjust permitted
                scenario inputs without changing the published Forecast itself.
              </Alert>

              <SimulationTabs
                value={this.state.activeTab}
                actions={w.actions}
                blockingcount={w.readiness.blockingcount}
                onChange={this.changeTab}
                // onChange={(activeTab) => {
                //   this.setState({ activeTab });
                  
                //   if (activeTab === 'objectivegap') {
                //     void this.loadCurrentObjectiveGap(false);
                //   }
                // }}
              />

              <Box>{this.renderScenarioTab()}</Box>
            </>
          )}
        </Stack>
      </PlanningWorkbenchShell>
    );
  }
}

export default InteractiveSimulationWorkbench;
